import React from 'react';
import { Outlet } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Box, Container } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Home as HomeIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { LanguageSwitcher } from './LanguageSwitcher';
import { companyApi } from '../services/api';
import { TENANT_COMPANY_NAME } from '../utils/runtimeUrls';
import { APP_ROUTES } from '../routing/appRoutes';
import { BrandMark } from './BrandMark';

const RENTMAN_WEBSITE_URL = 'https://rentman.al';

const rentmanBrandSx = {
  color: 'inherit',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  '&:hover': { opacity: 0.94 },
};

export const Layout: React.FC = () => {
  const { user, logout, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const isCompanyUser =
    user?.role === 'SUPERUSER' || user?.role === 'ADMIN' || user?.role === 'USER';

  const { data: tenantCompany } = useQuery('tenantCompanyProfile', companyApi.getTenant, {
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnMount: true,
  });

  const handleLogout = () => {
    logout();
    navigate(APP_ROUTES.public.search);
  };

  const handleLogin = () => {
    navigate(APP_ROUTES.auth.login);
  };

  const handleSearch = () => {
    navigate(APP_ROUTES.public.search);
  };

  const handleCompanyHome = () => {
    navigate(APP_ROUTES.company.dashboard);
  };

  const companyDisplayName =
    tenantCompany?.companyName || user?.company?.name || TENANT_COMPANY_NAME;

  const showAuthenticatedActions = !loading && isAuthenticated && !!user;

  const renderAccountAction = () => {
    if (!showAuthenticatedActions) {
      return (
        <Button color="inherit" onClick={handleLogin}>
          {t('auth.login')}
        </Button>
      );
    }

    if (user?.role === 'SUPERUSER' || user?.role === 'ADMIN' || user?.role === 'USER') {
      return (
        <Button color="inherit" onClick={handleCompanyHome} startIcon={<HomeIcon />}>
          {t('nav.home', 'Home')}
        </Button>
      );
    }

    return (
      <Button color="inherit" onClick={() => navigate(APP_ROUTES.user.dashboard)}>
        {t('nav.dashboard')}
      </Button>
    );
  };

  const renderRightActions = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'nowrap',
        '& .MuiButton-root': {
          color: 'text.primary',
          px: 1.2,
        },
      }}
    >
      {isCompanyUser ? renderAccountAction() : null}
      <LanguageSwitcher />
      <Button color="inherit" onClick={handleSearch} startIcon={<SearchIcon />}>
        {t('common.search')}
      </Button>
      {!isCompanyUser ? renderAccountAction() : null}
      {showAuthenticatedActions ? (
        <Button color="inherit" onClick={handleLogout}>
          {t('auth.logout')}
        </Button>
      ) : null}
    </Box>
  );

  const renderRentmanBrand = () => (
    <Box component="a" href={RENTMAN_WEBSITE_URL} sx={rentmanBrandSx}>
      <BrandMark compact showTagline={false} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="sticky"
        color="transparent"
        elevation={0}
        sx={{
          backdropFilter: 'blur(16px)',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.9),
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 18px 48px rgba(15, 23, 42, 0.08)',
          color: 'text.primary',
        }}
      >
        <Toolbar
          sx={{
            display: { xs: 'flex', sm: 'none' },
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          {renderRentmanBrand()}
          {renderRightActions()}
        </Toolbar>

        <Toolbar
          sx={{
            display: { xs: 'none', sm: 'flex' },
            gap: 2,
          }}
        >
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            {renderRentmanBrand()}
          </Box>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flex: '1 1 0',
              textAlign: 'center',
              fontWeight: 700,
              minWidth: 0,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'text.primary',
            }}
          >
            {companyDisplayName}
          </Typography>

          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
            {renderRightActions()}
          </Box>
        </Toolbar>

        {companyDisplayName ? (
          <Box
            sx={{
              display: { xs: 'block', sm: 'none' },
              px: 2,
              pb: 1.25,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                maxWidth: '100%',
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {companyDisplayName}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </AppBar>

      <Container component="main" maxWidth={false} sx={{ flexGrow: 1, py: { xs: 3, md: 4 } }}>
        <Outlet />
      </Container>

      <Box
        component="footer"
        sx={{
          py: 3.5,
          px: 2.5,
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.94),
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="body2" color="text.secondary" align="center">
            {t('layout.footerCopyright', { year: currentYear })}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};
